import { asId, type RoomId, type WallId } from '@obraplan/core/domain/shared/ids';
import { roundQuantity } from '@obraplan/core/domain/schedule/unit-of-measure';
import {
  PARALLEL_TOLERANCE_DEGREES,
  polygonEdges,
  segmentLength,
  sharedRun,
  type Adjacency,
  type Point,
  type Segment,
} from '@obraplan/core/domain/takeoff/polygon';
import { GEOMETRY_DECIMALS, type Room } from '@obraplan/core/domain/takeoff/room';
import type {
  ElementSegment,
  StructuralElement,
} from '@obraplan/core/domain/takeoff/structural-element';
import {
  isWholeEdge,
  spanOf,
  SPAN_TOLERANCE,
  wallSideOf,
  withEdgeSpan,
  type EdgeSpan,
  type Opening,
  type Wall,
  type WallSide,
} from '@obraplan/core/domain/takeoff/wall';
import {
  cutShared,
  fractionAlong,
  intersectSpans,
  joinSpans,
  MIN_WALL_PIECE,
  pointAtFraction,
  projectedSpan,
  roundSpan,
  spanLength,
  spansTouch,
  subSegment,
  type CutSide,
  type SharedCut,
} from '@obraplan/core/domain/takeoff/wall-span';
import { placeWalls } from './plan-tracer';
import type { TypologyDraft, WallLabel } from './typology-draft';

/**
 * Casar paredes à mão e cortar a sobreposição parcial (Fase 24, fatia 8 · D24 · RN-304 a
 * RN-307 · ADR-157).
 *
 * > As paredes que se casam em um mesmo ambiente não possuem opção hoje no sistema de
 * > casá-las.
 *
 * Faltavam duas coisas. A primeira é o **corte**: o casamento casava a parede inteira, e a
 * sala de 6 m com um quarto de 3 m encostado no meio ganhava ou perdia 3 m de alvenaria —
 * ou ficava com duas paredes, uma sobre a outra, as duas contadas. Agora a mais comprida é
 * cortada nos cantos da mais curta: o trecho comum vira a parede casada, e o resto continua
 * parede da sala, no mesmo lado do contorno, cada pedaço no trecho dele (`WallSide.span`).
 *
 * A segunda é o **casamento à mão**: o automático só casa o que está a até 30 cm
 * (`WALL_PAIRING`), e a parede grossa, ou traçada com a mão um pouco fora, ficava livre dos
 * dois lados, sem caminho na planta para dizer que era uma só.
 *
 * Mora fora do `draftReducer` porque é a mesma conta para vários caminhos — o casamento da
 * tipologia, o automático, a divisa entre tipologias, o descasamento e o empurrão — e cada
 * um guarda as paredes num lugar diferente. Aqui entra o rascunho de **uma** tipologia;
 * quem junta duas é a bancada.
 */

/** O pedaço do rascunho que o corte mexe. */
export type Pecas = Pick<
  TypologyDraft,
  'rooms' | 'walls' | 'openings' | 'elements' | 'elementSegments' | 'labels'
>;

function novoId<B extends string>(): ReturnType<typeof asId<B>> {
  return asId<B>(crypto.randomUUID());
}

/**
 * A folga do casamento feito à mão: **1 m** (RN-306).
 *
 * O automático para nos 30 cm de propósito — além disso há corredor e shaft, e casar
 * sozinho seria errar calado. À mão quem decide é a pessoa, e a lista só precisa não
 * oferecer a parede do outro lado da sala: um metro alcança a parede grossa traçada pelas
 * duas faces e o traço que escorregou, e as candidatas vêm da mais perto para a mais longe.
 */
export const MANUAL_PAIRING_GAP = 1;

export const MANUAL_PAIRING: Adjacency = {
  gap: MANUAL_PAIRING_GAP,
  angle: PARALLEL_TOLERANCE_DEGREES,
};

/** O recado de quem tentou casar duas paredes que não correm juntas. */
export const SEM_TRECHO_COMUM =
  'Estas duas paredes não correm juntas em trecho nenhum — não há o que casar entre elas.';

// ── Onde a parede está ──────────────────────────────────────────────────────────

/** Um lado da parede no desenho: o lado do contorno, e o trecho que ela ocupa nele. */
interface NoLado {
  readonly edgeIndex: number;
  /** O lado do contorno inteiro. */
  readonly edge: Segment;
  readonly span: EdgeSpan;
}

/** Onde este lado da parede está — `undefined` para quem não tem desenho. */
function noLado(rooms: readonly Room[], side: WallSide | undefined): NoLado | undefined {
  if (side?.edgeIndex === undefined) return undefined;
  const outline = rooms.find((room) => room.id === side.roomId)?.outline;
  const edge = outline === undefined ? undefined : polygonEdges(outline)[side.edgeIndex];
  return edge === undefined ? undefined : { edgeIndex: side.edgeIndex, edge, span: spanOf(side) };
}

/** As paredes **livres** deste ambiente que lembram este lado do contorno, na ordem do trecho. */
function livresNoLado(walls: readonly Wall[], roomId: RoomId, edgeIndex: number): Wall[] {
  return walls
    .filter(
      (wall) =>
        wall.deletedAt === undefined &&
        wall.sideB === undefined &&
        wall.sideA.roomId === roomId &&
        wall.sideA.edgeIndex === edgeIndex,
    )
    .sort((uma, outra) => spanOf(uma.sideA).start - spanOf(outra.sideA).start);
}

/** A parede no trecho, medindo o trecho — o comprimento de cada pedaço é o dele (RN-305). */
function comTrecho(wall: Wall, roomId: RoomId, edge: Segment, span: EdgeSpan): Wall {
  return { ...withEdgeSpan(wall, roomId, span), length: spanLength(edge, span) };
}

/** O nome do pedaço que sobra: o da parede de onde ele saiu, numerado no lado. */
function nomeDoPedaco(
  labels: Readonly<Record<string, WallLabel>>,
  walls: readonly Wall[],
  original: Wall,
  roomId: RoomId,
): WallLabel {
  const base = (labels[original.id]?.a ?? 'Parede').replace(/ · trecho \d+$/, '');
  const usados = walls.filter(
    (wall) =>
      wallSideOf(wall, roomId)?.edgeIndex === wallSideOf(original, roomId)?.edgeIndex &&
      / · trecho \d+$/.test(labels[wall.id]?.a ?? ''),
  ).length;
  return { a: `${base} · trecho ${String(usados + 1)}` };
}

// ── O vão e a peça andam com o lugar, e não com a parede ────────────────────────

/** Onde o desenho põe cada parede — o "começo" a partir do qual o vão mede a posição dele. */
type Moldura = (wallId: WallId) => Segment | undefined;

function molduraDoDesenho(pecas: Pick<Pecas, 'rooms' | 'walls'>): Moldura {
  const postas = new Map(
    placeWalls(pecas.rooms, pecas.walls).map((posta) => [String(posta.wallId), posta.segment]),
  );
  return (wallId) => postas.get(String(wallId));
}

function direcao(segment: Segment): Point {
  const comprimento = segmentLength(segment);
  if (comprimento === 0) return { x: 1, y: 0 };
  return {
    x: (segment.to.x - segment.from.x) / comprimento,
    y: (segment.to.y - segment.from.y) / comprimento,
  };
}

/** O ponto a `offset` metros do começo, `lateral` para a esquerda — como o desenho o põe. */
function pontoNaParede(moldura: Segment, offset: number, lateral = 0): Point {
  const u = direcao(moldura);
  const andado = Math.min(Math.max(offset, 0), segmentLength(moldura));
  return {
    x: moldura.from.x + u.x * andado - u.y * lateral,
    y: moldura.from.y + u.y * andado + u.x * lateral,
  };
}

function posicaoNaParede(moldura: Segment, ponto: Point): { offset: number; lateral: number } {
  const u = direcao(moldura);
  const dx = ponto.x - moldura.from.x;
  const dy = ponto.y - moldura.from.y;
  return {
    offset: roundQuantity(Math.min(Math.max(dx * u.x + dy * u.y, 0), segmentLength(moldura)), 3),
    lateral: roundQuantity(-dx * u.y + dy * u.x, 3),
  };
}

const chaveDoVao = (opening: Opening): string => `vao:${String(opening.id)}`;
const chaveDoTrecho = (segment: ElementSegment): string => `trecho:${String(segment.id)}`;

/**
 * Onde, no plano, está cada vão e cada peça posicionados nestas paredes (RN-307).
 *
 * O `offset` é metro **a partir do começo da parede**, e o começo muda quando a parede é
 * cortada, casada, descasada ou juntada: o pedaço do meio começa 1,5 m depois, e a casada é
 * desenhada sob o ambiente que vem primeiro, às vezes de trás para frente. Guardar o ponto
 * antes e medi-lo de novo depois deixa a porta onde ela foi posta, qualquer que seja a
 * parede que a leve.
 */
function ancorar(
  pecas: Pick<Pecas, 'openings' | 'elementSegments'>,
  moldura: Moldura,
  paredes: ReadonlySet<WallId>,
): Map<string, Point> {
  const ancoras = new Map<string, Point>();
  for (const opening of pecas.openings) {
    const lugar = paredes.has(opening.wallId) ? moldura(opening.wallId) : undefined;
    if (lugar === undefined || opening.offset === undefined) continue;
    ancoras.set(chaveDoVao(opening), pontoNaParede(lugar, opening.offset));
  }
  for (const segment of pecas.elementSegments) {
    const lugar = paredes.has(segment.wallId) ? moldura(segment.wallId) : undefined;
    if (lugar === undefined || segment.offset === undefined) continue;
    ancoras.set(chaveDoTrecho(segment), pontoNaParede(lugar, segment.offset, segment.lateral));
  }
  return ancoras;
}

/** O vão e a peça medidos de novo, cada um na parede que o leva agora. */
function reposicionar<T extends Pick<Pecas, 'openings' | 'elementSegments'>>(
  pecas: T,
  moldura: Moldura,
  ancoras: ReadonlyMap<string, Point>,
): T {
  if (ancoras.size === 0) return pecas;
  return {
    ...pecas,
    openings: pecas.openings.map((opening) => {
      const ponto = ancoras.get(chaveDoVao(opening));
      const lugar = ponto === undefined ? undefined : moldura(opening.wallId);
      if (ponto === undefined || lugar === undefined) return opening;
      return { ...opening, offset: posicaoNaParede(lugar, ponto).offset };
    }),
    elementSegments: pecas.elementSegments.map((segment) => {
      const ponto = ancoras.get(chaveDoTrecho(segment));
      const lugar = ponto === undefined ? undefined : moldura(segment.wallId);
      if (ponto === undefined || lugar === undefined) return segment;
      const { offset, lateral } = posicaoNaParede(lugar, ponto);
      // Só quem já dizia o lado continua dizendo: o trecho no eixo segue no eixo.
      return segment.lateral === undefined
        ? { ...segment, offset }
        : { ...segment, offset, lateral };
    }),
  };
}

/**
 * O vão e a peça destas paredes, no mesmo lugar do plano depois da mudança (RN-307).
 *
 * Para quem muda a parede de lugar sem passar pelo corte — o ambiente apagado deixa a
 * casada só com o lado que sobrou, e ela passa a ser desenhada sob ele, às vezes de trás
 * para frente.
 */
export function mantendoOLugar<T extends Pecas>(
  antes: Pecas,
  paredes: readonly WallId[],
  depois: T,
): T {
  const ancoras = ancorar(antes, molduraDoDesenho(antes), new Set(paredes));
  return reposicionar(depois, molduraDoDesenho(depois), ancoras);
}

/**
 * Um trecho por peça em cada parede — o banco recusa o segundo (`element_segments_uq`).
 *
 * Acontece quando duas paredes viram uma: a viga deitada sobre a parede da sala e sobre a
 * do quarto, que eram a mesma parede vista dos dois lados, chegaria com dois trechos na
 * casada. Fica o primeiro, que é o da parede que ficou.
 */
function semTrechoRepetido(segments: readonly ElementSegment[]): ElementSegment[] {
  const vistos = new Set<string>();
  return segments.filter((segment) => {
    const chave = `${String(segment.elementId)}|${String(segment.wallId)}`;
    if (vistos.has(chave)) return false;
    vistos.add(chave);
    return true;
  });
}

/** A viga deitada sobre a parede — a que se reparte entre os pedaços, em vez de ir para um. */
function vigaDeitada(segment: ElementSegment, elements: readonly StructuralElement[]): boolean {
  if (segment.orientation !== 'PARALELA') return false;
  return elements.find((element) => element.id === segment.elementId)?.kind === 'VIGA';
}

// ── Cortar ──────────────────────────────────────────────────────────────────────

/**
 * Corta uma parede **livre** no trecho comum: o comum fica com a identidade dela, e cada
 * sobra vira parede livre nova do mesmo ambiente, no mesmo lado, com o trecho dela (RN-305).
 *
 * O comum fica com o id porque é ele que casa: o nome que alguém deu e o vão cadastrado no
 * cartão — que não diz onde fica na parede — seguem a parede que continua sendo "a parede
 * entre a sala e o quarto". As sobras herdam altura, espessura e mureta.
 *
 * O vão e a peça posicionados vão para o pedaço em que estão, medidos de novo a partir do
 * começo dele. A viga deitada é a exceção: ela corre o comprimento dela, e o pedaço de viga
 * que cai em cada pedaço de parede vira um trecho ali — a viga da parede inteira passa a ser
 * três trechos, e a soma deles é a mesma viga.
 *
 * Sem sobra nenhuma, a parede fica como está — inclusive a medida corrigida no painel.
 */
export function cortarParede<T extends Pecas>(draft: T, wallId: WallId, corte: CutSide): T {
  if (corte.before === undefined && corte.after === undefined) return draft;
  const wall = draft.walls.find((candidate) => candidate.id === wallId);
  if (wall === undefined || wall.sideB !== undefined) return draft;
  const roomId = wall.sideA.roomId;
  const geo = noLado(draft.rooms, wall.sideA);
  if (geo === undefined) return draft;

  const now = new Date().toISOString();
  const { label: _nome, notes: _notas, ...semNome } = wall;
  const pedacos: { readonly id: WallId; readonly span: EdgeSpan }[] = [
    ...(corte.before === undefined ? [] : [{ id: novoId<'Wall'>(), span: corte.before }]),
    { id: wall.id, span: corte.common },
    ...(corte.after === undefined ? [] : [{ id: novoId<'Wall'>(), span: corte.after }]),
  ];

  let walls = draft.walls.map((candidate) =>
    candidate.id === wall.id
      ? comTrecho({ ...candidate, updatedAt: now }, roomId, geo.edge, corte.common)
      : candidate,
  );
  const labels: Record<string, WallLabel> = { ...draft.labels };
  for (const pedaco of pedacos) {
    if (pedaco.id === wall.id) continue;
    labels[pedaco.id] = nomeDoPedaco(labels, walls, wall, roomId);
    walls = [
      ...walls,
      comTrecho(
        { ...semNome, id: pedaco.id, createdAt: now, updatedAt: now },
        roomId,
        geo.edge,
        pedaco.span,
      ),
    ];
  }

  // Em que pedaço cai o ponto do lado: o que o contém, ou o mais perto quando ele cai na
  // sobra de um clique entre dois.
  const medida = segmentLength(geo.edge);
  const fora = (fracao: number, span: EdgeSpan): number =>
    Math.max(0, span.start - fracao, fracao - span.end);
  const pedacoEm = (fracao: number) =>
    pedacos.reduce((melhor, pedaco) =>
      fora(fracao, pedaco.span) < fora(fracao, melhor.span) ? pedaco : melhor,
    );
  const fracaoDe = (offset: number): number => geo.span.start + offset / medida;
  const noPedaco = (fracao: number, span: EdgeSpan): number =>
    roundQuantity(Math.max(0, (fracao - span.start) * medida), 3);

  const openings = draft.openings.map((opening): Opening => {
    if (opening.wallId !== wall.id || opening.offset === undefined) return opening;
    const fracao = fracaoDe(opening.offset);
    const destino = pedacoEm(fracao);
    return { ...opening, wallId: destino.id, offset: noPedaco(fracao, destino.span) };
  });

  const elementSegments = draft.elementSegments.flatMap((segment): ElementSegment[] => {
    if (segment.wallId !== wall.id) return [segment];

    if (vigaDeitada(segment, draft.elements)) {
      // A viga da parede inteira cobre cada pedaço inteiro.
      if (segment.length === undefined) {
        return pedacos.map((pedaco) => ({
          ...segment,
          ...(pedaco.id === wall.id ? {} : { id: novoId<'ElementSegment'>() }),
          wallId: pedaco.id,
          ...(segment.offset === undefined
            ? {}
            : { offset: roundQuantity(((pedaco.span.end - pedaco.span.start) * medida) / 2, 3) }),
        }));
      }
      // A viga de um pedaço da parede: o pedaço dela que cai em cada pedaço de parede. Sem
      // posição não há como saber qual, e ela fica com a parede que continua.
      if (segment.offset === undefined) return [segment];
      const meio = segment.length / 2 / medida;
      const centro = fracaoDe(segment.offset);
      const corrida = { start: centro - meio, end: centro + meio };
      const partes = pedacos.flatMap((pedaco) => {
        const comum = intersectSpans(corrida, pedaco.span);
        return comum === undefined || (comum.end - comum.start) * medida < 0.01
          ? []
          : [{ pedaco, comum }];
      });
      if (partes.length === 0) return [segment];
      const maior = partes.reduce((uma, outra) =>
        outra.comum.end - outra.comum.start > uma.comum.end - uma.comum.start ? outra : uma,
      );
      return partes.map(({ pedaco, comum }) => ({
        ...segment,
        ...(maior.pedaco === pedaco ? {} : { id: novoId<'ElementSegment'>() }),
        wallId: pedaco.id,
        length: roundQuantity((comum.end - comum.start) * medida, GEOMETRY_DECIMALS),
        offset: noPedaco((comum.start + comum.end) / 2, pedaco.span),
      }));
    }

    if (segment.offset === undefined) return [segment];
    const fracao = fracaoDe(segment.offset);
    const destino = pedacoEm(fracao);
    return [{ ...segment, wallId: destino.id, offset: noPedaco(fracao, destino.span) }];
  });

  return { ...draft, walls, openings, elementSegments, labels };
}

// ── Casar ───────────────────────────────────────────────────────────────────────

/**
 * O corte das duas paredes no trecho em que elas correm juntas.
 *
 * - `undefined`: falta desenho a uma delas (ambiente digitado, parede de antes do índice), e
 *   o casamento é o de sempre, a parede inteira;
 * - `SEM_TRECHO`: as duas têm desenho e não correm juntas em pedaço nenhum — casá-las seria
 *   dizer que são a mesma parede duas paredes que não se encostam;
 * - o corte, com o trecho comum e as sobras de cada lado.
 */
export function corteParaCasar(
  roomsDaQueFica: readonly Room[],
  keep: Wall,
  roomsDaQueSome: readonly Room[],
  drop: Wall,
): SharedCut | 'SEM_TRECHO' | undefined {
  const a = noLado(roomsDaQueFica, keep.sideA);
  const b = noLado(roomsDaQueSome, drop.sideA);
  if (a === undefined || b === undefined) return undefined;
  return cutShared(a.edge, a.span, b.edge, b.span) ?? 'SEM_TRECHO';
}

/**
 * Casa duas paredes livres da mesma tipologia, cortando o que sobra de cada uma (RN-305).
 *
 * `casar` é o casamento de sempre — o lado da que some vira o lado B da que fica, com os
 * vãos e a estrutura dela —, e quem o faz é o `draftReducer`. Aqui ficam o corte antes e a
 * posição do vão depois: a casada é desenhada sob o primeiro dos dois ambientes, e o vão de
 * cada uma precisa ser medido de novo a partir do começo dela.
 */
export function casarComCorte(
  draft: TypologyDraft,
  keep: Wall,
  drop: Wall,
  casar: (cortado: TypologyDraft) => TypologyDraft,
): TypologyDraft | 'SEM_TRECHO' {
  const corte = corteParaCasar(draft.rooms, keep, draft.rooms, drop);
  if (corte === 'SEM_TRECHO') return corte;

  const cortado =
    corte === undefined
      ? draft
      : cortarParede(cortarParede(draft, keep.id, corte.a), drop.id, corte.b);
  const ancoras = ancorar(cortado, molduraDoDesenho(cortado), new Set([keep.id, drop.id]));
  const casado = casar(cortado);
  // A recusa vem sobre o rascunho de antes do corte: casamento recusado não corta nada.
  if (casado.lastError !== undefined) return { ...draft, lastError: casado.lastError };
  const reposto = reposicionar(casado, molduraDoDesenho(casado), ancoras);
  return { ...reposto, elementSegments: semTrechoRepetido(reposto.elementSegments) };
}

/**
 * O vão e a peça da parede que vai para a divisa, medidos a partir do começo da que fica.
 *
 * A divisa não é desenhada em tipologia nenhuma — ela mora na bancada —, e o começo dela é o
 * do lado A, a parede que fica: é para lá que os vãos voltam quando ela é desfeita
 * (`desfazerDivisa`). O vão que vinha do lado B passa a ser medido dali.
 */
export function medirNaQueFica<T extends Pecas>(
  daQueSome: T,
  drop: Wall,
  keep: Wall,
  roomsDaQueFica: readonly Room[],
): T {
  const de = noLado(daQueSome.rooms, drop.sideA);
  const para = noLado(roomsDaQueFica, keep.sideA);
  if (de === undefined || para === undefined) return daQueSome;
  const ancoras = ancorar(
    daQueSome,
    (id) => (id === drop.id ? subSegment(de.edge, de.span) : undefined),
    new Set([drop.id]),
  );
  return reposicionar(
    daQueSome,
    (id) => (id === drop.id ? subSegment(para.edge, para.span) : undefined),
    ancoras,
  );
}

/**
 * Descasa: a parede volta a ser livre do lado A, e a do lado B renasce livre no dela.
 *
 * O vão fica com o lado A, como sempre ficou — e é medido de novo, porque a casada era
 * desenhada sob o primeiro dos dois ambientes, que podia ser o do outro lado. Depois cada
 * uma se junta aos pedaços livres vizinhos do mesmo lado: o corte foi feito para casar, e
 * descasado ele não tem mais motivo (RN-305).
 */
export function descasarEJuntar(
  draft: TypologyDraft,
  wallId: WallId,
  descasar: (antes: TypologyDraft) => TypologyDraft,
): TypologyDraft {
  const ancoras = ancorar(draft, molduraDoDesenho(draft), new Set([wallId]));
  const separado = descasar(draft);
  if (separado.lastError !== undefined) return separado;
  const existiam = new Set(draft.walls.map((wall) => String(wall.id)));
  const renascidas = separado.walls.filter((wall) => !existiam.has(String(wall.id)));

  let resultado = reposicionar(separado, molduraDoDesenho(separado), ancoras);
  resultado = juntarPedacos(resultado, wallId);
  for (const renascida of renascidas) resultado = juntarPedacos(resultado, renascida.id);
  return resultado;
}

/**
 * O comprimento do trecho deste lado, quando ele foi cortado — o da parede que renasce nele.
 *
 * Só do lado cortado: no lado inteiro a medida da casada é a medida dele, e ela pode ter sido
 * corrigida no painel — o número que alguém conferiu na trena não volta para o do traço.
 */
export function medidaDoTrecho(rooms: readonly Room[], side: WallSide): number | undefined {
  if (side.span === undefined) return undefined;
  const geo = noLado(rooms, side);
  return geo === undefined ? undefined : spanLength(geo.edge, geo.span);
}

// ── Juntar ──────────────────────────────────────────────────────────────────────

/** Duas paredes que podem voltar a ser uma: a mesma altura, a mesma espessura, a mesma mureta. */
function juntaveis(uma: Wall, outra: Wall): boolean {
  return (
    !uma.joined &&
    !outra.joined &&
    uma.height === outra.height &&
    uma.thickness === outra.thickness &&
    uma.railingHeight === outra.railingHeight
  );
}

/**
 * Junta a parede livre aos pedaços livres que encostam nela no mesmo lado (RN-305).
 *
 * É o caminho de volta do corte: descasada a parede do meio, os três pedaços da sala voltam
 * a ser uma parede de 6 m — com a identidade desta, e os vãos e a estrutura dos outros
 * medidos de novo a partir do começo dela. Só junta o que é igual: a mureta de 1,10 ao lado
 * da parede de 2,90 são duas paredes de verdade, e somá-las numa só mudaria a alvenaria.
 */
export function juntarPedacos<T extends Pecas>(draft: T, wallId: WallId): T {
  const wall = draft.walls.find((candidate) => candidate.id === wallId);
  if (wall === undefined || wall.sideB !== undefined || wall.sideA.edgeIndex === undefined) {
    return draft;
  }
  const roomId = wall.sideA.roomId;
  const geo = noLado(draft.rooms, wall.sideA);

  const irmas = livresNoLado(draft.walls, roomId, wall.sideA.edgeIndex).filter(
    (outra) => outra.id !== wall.id && juntaveis(wall, outra),
  );
  let trecho = spanOf(wall.sideA);
  const juntadas: Wall[] = [];
  for (let achou = true; achou;) {
    achou = false;
    for (const outra of irmas) {
      if (juntadas.includes(outra) || !spansTouch(trecho, spanOf(outra.sideA))) continue;
      trecho = joinSpans(trecho, spanOf(outra.sideA));
      juntadas.push(outra);
      achou = true;
    }
  }
  if (juntadas.length === 0) return draft;

  const pedacos = [wall, ...juntadas];
  const fora = new Set(juntadas.map((outra) => outra.id));
  const ancoras = ancorar(draft, molduraDoDesenho(draft), new Set([wall.id, ...fora]));
  // A soma das medidas, e não a do lado: o pedaço corrigido no painel continua corrigido.
  const comprimento = roundQuantity(
    pedacos.reduce((soma, parede) => soma + parede.length, 0),
    GEOMETRY_DECIMALS,
  );
  const unida: Wall = {
    ...withEdgeSpan(wall, roomId, trecho),
    length: comprimento,
    updatedAt: new Date().toISOString(),
  };

  const labels: Record<string, WallLabel> = { ...draft.labels };
  for (const id of fora) delete labels[id];

  const { segments, refeitos } = juntarVigas(draft, wall.id, pedacos, trecho, geo);
  for (const id of refeitos) ancoras.delete(`trecho:${id}`);

  const movido: T = {
    ...draft,
    walls: draft.walls
      .filter((parede) => !fora.has(parede.id))
      .map((parede) => (parede.id === wall.id ? unida : parede)),
    openings: draft.openings.map((opening) =>
      fora.has(opening.wallId) ? { ...opening, wallId: wall.id } : opening,
    ),
    elementSegments: segments,
    labels,
  };
  const reposto = reposicionar(movido, molduraDoDesenho(movido), ancoras);
  return { ...reposto, elementSegments: semTrechoRepetido(reposto.elementSegments) };
}

/**
 * Os trechos das paredes juntadas, levados à parede que ficou — e a viga que o corte
 * repartiu, de volta num trecho só.
 *
 * Os pedaços da viga deitada em cada pedaço de parede viram um: a corrida é a soma deles, e
 * o meio, o do que eles cobrem. Cobrindo todos os pedaços inteiros, é a viga da parede
 * inteira de novo — sem corrida, como ela nasceu.
 */
function juntarVigas(
  draft: Pick<Pecas, 'elements' | 'elementSegments'>,
  wallId: WallId,
  pedacos: readonly Wall[],
  trecho: EdgeSpan,
  geo: NoLado | undefined,
): { readonly segments: ElementSegment[]; readonly refeitos: ReadonlySet<string> } {
  const doPedaco = new Map(pedacos.map((pedaco) => [String(pedaco.id), pedaco]));
  const porPeca = new Map<string, ElementSegment[]>();
  for (const segment of draft.elementSegments) {
    if (!doPedaco.has(String(segment.wallId)) || !vigaDeitada(segment, draft.elements)) continue;
    porPeca.set(String(segment.elementId), [
      ...(porPeca.get(String(segment.elementId)) ?? []),
      segment,
    ]);
  }

  const substituir = new Map<string, ElementSegment | undefined>();
  const medida = geo === undefined ? 0 : segmentLength(geo.edge);
  for (const lista of porPeca.values()) {
    const [primeiro, ...outros] = lista;
    if (primeiro === undefined || outros.length === 0) continue;

    const inteiros = lista.every((segment) => segment.length === undefined);
    const cobreTudo = pedacos.every((pedaco) =>
      lista.some((segment) => segment.wallId === pedaco.id),
    );
    let unido: ElementSegment;
    if (inteiros && cobreTudo) {
      const { length: _corrida, offset: _meio, ...semCorrida } = primeiro;
      unido = { ...semCorrida, wallId };
    } else if (geo === undefined) {
      unido = { ...primeiro, wallId };
    } else {
      // Cada pedaço de viga em metros do começo do lado: o pedaço de parede dele começa onde
      // o trecho dele começa, e a posição da viga é medida dali.
      const corridas = lista.map((segment) => {
        const span = spanOf(doPedaco.get(String(segment.wallId))?.sideA);
        const daParede = (span.end - span.start) * medida;
        const comprimento = segment.length ?? daParede;
        const centro = span.start * medida + (segment.offset ?? daParede / 2);
        return { inicio: centro - comprimento / 2, fim: centro + comprimento / 2, comprimento };
      });
      const inicio = Math.min(...corridas.map((corrida) => corrida.inicio));
      const fim = Math.max(...corridas.map((corrida) => corrida.fim));
      unido = {
        ...primeiro,
        wallId,
        length: roundQuantity(
          corridas.reduce((soma, corrida) => soma + corrida.comprimento, 0),
          GEOMETRY_DECIMALS,
        ),
        offset: roundQuantity((inicio + fim) / 2 - trecho.start * medida, 3),
      };
    }
    substituir.set(String(primeiro.id), unido);
    for (const outro of outros) substituir.set(String(outro.id), undefined);
  }

  const segments = draft.elementSegments.flatMap((segment): ElementSegment[] => {
    if (substituir.has(String(segment.id))) {
      const novo = substituir.get(String(segment.id));
      return novo === undefined ? [] : [novo];
    }
    return doPedaco.has(String(segment.wallId)) ? [{ ...segment, wallId }] : [segment];
  });
  return { segments, refeitos: new Set(substituir.keys()) };
}

// ── O empurrão e o redesenho ────────────────────────────────────────────────────

/**
 * O trecho que a parede ocupava no lado de antes, no lado depois do empurrão.
 *
 * As pontas do lado ficam nas pontas — o pedaço que chegava ao canto continua chegando —, e
 * as do meio ficam onde estavam no plano: empurrar a parede da esquerda da sala não leva
 * junto o quarto encostado no meio da de cima.
 *
 * `undefined` é o pedaço que **saiu do lado**: a parede da esquerda empurrada para dentro,
 * além do canto do quarto, deixa fora do lado novo o pedaço livre que ia até ela. Devolver o
 * trecho de antes o deixaria por cima do vizinho, com a alvenaria contada duas vezes; quem
 * decide o que fazer com ele é quem empurrou (`semOsPedacosQueSairam`).
 */
export function trechoEmpurrado(
  antes: Segment,
  depois: Segment,
  span: EdgeSpan,
): EdgeSpan | undefined {
  if (isWholeEdge(span)) return span;
  const mapa = (fracao: number): number => {
    if (fracao <= SPAN_TOLERANCE) return 0;
    if (fracao >= 1 - SPAN_TOLERANCE) return 1;
    return roundSpan(fractionAlong(depois, pointAtFraction(antes, fracao)));
  };
  const start = mapa(span.start);
  const end = mapa(span.end);
  return end - start > SPAN_TOLERANCE ? { start, end } : undefined;
}

/**
 * Quanto o ponto fica fora da parede, medido **ao longo** dela — zero se ele cai nela.
 *
 * Ao longo, e não em linha reta: a casada é desenhada sob um dos dois ambientes, às vezes a
 * 15 cm do lado do outro, e a distância em linha reta mandaria a porta para o pedaço do
 * vizinho só por ele estar mais perto de lado.
 */
/** Um milímetro: o ponto em cima da fronteira ainda é da parede em que estava. */
const DENTRO_DA_PAREDE = 0.001;

function foraDaParede(moldura: Segment, ponto: Point): number {
  const fracao = fractionAlong(moldura, ponto);
  return Math.max(0, -fracao, fracao - 1) * segmentLength(moldura);
}

/**
 * Os pedaços livres que o empurrão tirou do lado saem do desenho (RN-305).
 *
 * O vão e a peça que estavam neles não somem com eles: passam para a parede do mesmo lado
 * que ficou com aquele lugar — a que chega ao canto para onde o pedaço foi empurrado —,
 * medidos de novo a partir do lugar de antes, que agora cai no fim dela.
 */
export function semOsPedacosQueSairam<T extends Pecas>(
  antes: Pecas,
  depois: T,
  roomId: RoomId,
  sairam: ReadonlySet<WallId>,
): T {
  const room = depois.rooms.find((candidate) => candidate.id === roomId);
  if (sairam.size === 0 || room === undefined) return depois;

  const postas = placeWalls([room], depois.walls);
  const molduraDeAntes = molduraDoDesenho(antes);
  const destino = new Map<WallId, WallId>();
  for (const id of sairam) {
    const posta = postas.find((candidata) => candidata.wallId === id);
    const velha = molduraDeAntes(id);
    if (posta === undefined || velha === undefined) continue;
    const meio = pontoNaParede(velha, segmentLength(velha) / 2);
    const ficou = postas
      .filter((outra) => outra.edgeIndex === posta.edgeIndex && !sairam.has(outra.wallId))
      .reduce<(typeof postas)[number] | undefined>(
        (melhor, outra) =>
          melhor === undefined ||
          foraDaParede(outra.segment, meio) < foraDaParede(melhor.segment, meio)
            ? outra
            : melhor,
        undefined,
      );
    if (ficou !== undefined) destino.set(id, ficou.wallId);
  }

  // Sem parede que fique com o lugar, o pedaço fica: sumir com ele levaria junto o vão.
  if (destino.size === 0) return depois;
  const labels: Record<string, WallLabel> = { ...depois.labels };
  for (const id of destino.keys()) delete labels[id];
  const semEles: T = {
    ...depois,
    walls: depois.walls.filter((wall) => !destino.has(wall.id)),
    openings: depois.openings.map((opening) => {
      const para = destino.get(opening.wallId);
      return para === undefined ? opening : { ...opening, wallId: para };
    }),
    elementSegments: semTrechoRepetido(
      depois.elementSegments.map((segment) => {
        const para = destino.get(segment.wallId);
        return para === undefined ? segment : { ...segment, wallId: para };
      }),
    ),
    labels,
  };
  return mantendoOLugar(antes, [...destino.keys()], semEles);
}

/**
 * Até onde a casada pode crescer neste lado: o trecho dela mais os pedaços livres que
 * encostam, até o canto ou até outra parede que não seja livre.
 */
function envelope(walls: readonly Wall[], wall: Wall, roomId: RoomId, edgeIndex: number): EdgeSpan {
  const livres = livresNoLado(walls, roomId, edgeIndex).filter((outra) => outra.id !== wall.id);
  let trecho = spanOf(wallSideOf(wall, roomId));
  for (let achou = true; achou;) {
    achou = false;
    for (const livre of livres) {
      const dela = spanOf(livre.sideA);
      if (dela.start >= trecho.start && dela.end <= trecho.end) continue;
      if (!spansTouch(trecho, dela)) continue;
      trecho = joinSpans(trecho, dela);
      achou = true;
    }
  }
  return trecho;
}

/**
 * A casada depois de um dos dois lados mudar de lugar: o trecho comum medido de novo, e os
 * pedaços livres vizinhos acompanhando (RN-305).
 *
 * O quarto alargado meio metro passa a encostar meio metro a mais da sala: a casada cresce
 * para dentro do pedaço livre da sala, que encolhe. O quarto estreitado deixa um pedaço da
 * sala sem vizinho, e ele volta a ser parede livre dela. Sem isto o empurrão deixava a
 * casada e o pedaço livre um sobre o outro — a alvenaria contada duas vezes, o defeito que o
 * corte veio tirar.
 *
 * `redesenhado` é o ambiente cujo contorno acabou de ser traçado de novo: lá não há pedaço
 * nenhum ainda, e o trecho da casada é o que o vizinho cobre do lado novo.
 */
export function reajustarCasada<T extends Pecas>(
  draft: T,
  wallId: WallId,
  redesenhado?: RoomId,
): T {
  const wall = draft.walls.find((candidate) => candidate.id === wallId);
  if (wall?.sideB === undefined) return draft;
  const a = noLado(draft.rooms, wall.sideA);
  const b = noLado(draft.rooms, wall.sideB);
  if (a === undefined || b === undefined) return draft;

  const envelopeDe = (side: WallSide, geo: NoLado): EdgeSpan =>
    side.roomId === redesenhado
      ? { start: 0, end: 1 }
      : envelope(draft.walls, wall, side.roomId, geo.edgeIndex);
  const envA = envelopeDe(wall.sideA, a);
  const envB = envelopeDe(wall.sideB, b);
  const sombraB = projectedSpan(a.edge, subSegment(b.edge, envB));
  const sombraA = projectedSpan(b.edge, subSegment(a.edge, envA));
  const comumA = sombraB === undefined ? undefined : intersectSpans(envA, sombraB);
  const comumB = sombraA === undefined ? undefined : intersectSpans(envB, sombraA);
  // Os dois se afastaram de vez: não é o empurrão que decide descasar, e a casada fica como
  // estava até alguém desfazê-la.
  if (comumA === undefined || comumB === undefined) return draft;

  const envolvidas = new Set([
    wall.id,
    ...livresNoLado(draft.walls, wall.sideA.roomId, a.edgeIndex).map((livre) => livre.id),
    ...livresNoLado(draft.walls, wall.sideB.roomId, b.edgeIndex).map((livre) => livre.id),
  ]);
  const ancoras = ancorar(draft, molduraDoDesenho(draft), envolvidas);

  const ladoA = acomodarVizinhos(draft, wall, wall.sideA.roomId, a, comumA, redesenhado);
  const ladoB = acomodarVizinhos(ladoA.draft, wall, wall.sideB.roomId, b, comumB, redesenhado);
  const ajustado: T = {
    ...ladoB.draft,
    walls: ladoB.draft.walls.map((candidate) => {
      if (candidate.id !== wall.id) return candidate;
      const comA = withEdgeSpan(candidate, wall.sideA.roomId, ladoA.span);
      return {
        ...withEdgeSpan(comA, wall.sideB!.roomId, ladoB.span),
        length: spanLength(a.edge, ladoA.span),
      };
    }),
  };
  const comOsVaos = naParedeQueContem(ajustado, ancoras, wall, a.edgeIndex, b.edgeIndex);
  return reposicionar(comOsVaos, molduraDoDesenho(comOsVaos), ancoras);
}

/**
 * O vão e a peça vão para a parede que passou a conter o ponto deles (RN-307).
 *
 * A fronteira da casada anda no empurrão e no redesenho, e o ponto pode ter mudado de
 * parede: a porta em x = 5 no pedaço livre que o quarto alargado comeu passa a ser da
 * casada; a da casada que o quarto estreitado deixou volta para o pedaço livre da sala, e
 * deixa de ser descontada nas duas faces. Medir de novo só na parede de antes grampeava a
 * posição no fim dela, e o vão saía do lugar do plano.
 *
 * O pedaço livre troca só com a casada e com os pedaços do mesmo ambiente; a casada, com os
 * pedaços dos dois. A viga deitada fica: ela corre o comprimento dela, e quem a reparte entre
 * os pedaços é o corte.
 */
function naParedeQueContem<T extends Pecas>(
  draft: T,
  ancoras: ReadonlyMap<string, Point>,
  casada: Wall,
  edgeA: number,
  edgeB: number,
): T {
  if (ancoras.size === 0 || casada.sideB === undefined) return draft;
  const moldura = molduraDoDesenho(draft);
  const doA = livresNoLado(draft.walls, casada.sideA.roomId, edgeA).map((livre) => livre.id);
  const doB = livresNoLado(draft.walls, casada.sideB.roomId, edgeB).map((livre) => livre.id);
  const opcoesDe = (wallId: WallId): readonly WallId[] => {
    if (wallId === casada.id) return [...doA, ...doB];
    if (doA.includes(wallId)) return [casada.id, ...doA];
    if (doB.includes(wallId)) return [casada.id, ...doB];
    return [];
  };

  // A que contém o ponto; a de agora fica com ele enquanto contiver, e no empate também.
  const destino = (wallId: WallId, ponto: Point | undefined): WallId => {
    const atual = moldura(wallId);
    if (ponto === undefined || atual === undefined) return wallId;
    let melhor = { wallId, fora: foraDaParede(atual, ponto) };
    if (melhor.fora <= DENTRO_DA_PAREDE) return wallId;
    for (const outra of opcoesDe(wallId)) {
      const lugar = moldura(outra);
      const fora = lugar === undefined ? Infinity : foraDaParede(lugar, ponto);
      if (fora < melhor.fora - DENTRO_DA_PAREDE) melhor = { wallId: outra, fora };
    }
    return melhor.wallId;
  };

  return {
    ...draft,
    openings: draft.openings.map((opening) => {
      const para = destino(opening.wallId, ancoras.get(chaveDoVao(opening)));
      return para === opening.wallId ? opening : { ...opening, wallId: para };
    }),
    elementSegments: semTrechoRepetido(
      draft.elementSegments.map((segment) => {
        if (vigaDeitada(segment, draft.elements)) return segment;
        const para = destino(segment.wallId, ancoras.get(chaveDoTrecho(segment)));
        return para === segment.wallId ? segment : { ...segment, wallId: para };
      }),
    ),
  };
}

/**
 * Os pedaços livres que encostam na casada, acompanhando o trecho novo dela num lado.
 *
 * O vizinho de antes encolhe ou cresce até a fronteira nova; o que ficaria menor que
 * `MIN_WALL_PIECE` sai, e a casada fica com o lugar dele. Onde a casada recuou e não havia
 * vizinho, nasce um pedaço livre — o resto do lado que deixou de ter o outro ambiente. No
 * ambiente redesenhado não há vizinho a acompanhar: quem põe as paredes livres no que sobra
 * do lado novo é o redesenho.
 */
function acomodarVizinhos<T extends Pecas>(
  draft: T,
  casada: Wall,
  roomId: RoomId,
  geo: NoLado,
  novo: EdgeSpan,
  redesenhado: RoomId | undefined,
): { readonly draft: T; readonly span: EdgeSpan } {
  const medida = segmentLength(geo.edge);
  const absorver = (span: EdgeSpan, env: EdgeSpan): EdgeSpan => ({
    start: (span.start - env.start) * medida < MIN_WALL_PIECE ? env.start : roundSpan(span.start),
    end: (env.end - span.end) * medida < MIN_WALL_PIECE ? env.end : roundSpan(span.end),
  });
  if (roomId === redesenhado) return { draft, span: absorver(novo, { start: 0, end: 1 }) };

  const antes = geo.span;
  const livres = livresNoLado(draft.walls, roomId, geo.edgeIndex);
  const encosta = (valor: number, alvo: number): boolean =>
    Math.abs(valor - alvo) <= SPAN_TOLERANCE;
  const anterior = livres.find((livre) => encosta(spanOf(livre.sideA).end, antes.start));
  const seguinte = livres.find((livre) => encosta(spanOf(livre.sideA).start, antes.end));

  let span: EdgeSpan = { start: roundSpan(novo.start), end: roundSpan(novo.end) };
  let walls = [...draft.walls];
  const labels: Record<string, WallLabel> = { ...draft.labels };
  const removidas = new Set<WallId>();
  const now = new Date().toISOString();

  const ajustar = (livre: Wall | undefined, ponta: 'start' | 'end'): void => {
    const fronteira = span[ponta];
    const original = antes[ponta];
    if (encosta(fronteira, original)) return;

    if (livre !== undefined) {
      const dela = spanOf(livre.sideA);
      const trecho =
        ponta === 'start'
          ? { start: dela.start, end: fronteira }
          : { start: fronteira, end: dela.end };
      if ((trecho.end - trecho.start) * medida < MIN_WALL_PIECE) {
        // Encolheu até virar sobra: sai, e a casada fica com o lugar dele.
        removidas.add(livre.id);
        span =
          ponta === 'start'
            ? { ...span, start: Math.min(fronteira, dela.start) }
            : { ...span, end: Math.max(fronteira, dela.end) };
        return;
      }
      walls = walls.map((parede) =>
        parede.id === livre.id
          ? { ...comTrecho(parede, roomId, geo.edge, trecho), updatedAt: now }
          : parede,
      );
      return;
    }

    // Recuou de onde não havia vizinho livre: o que sobrou do lado volta a ser parede livre.
    const sobra =
      ponta === 'start' ? { start: original, end: fronteira } : { start: fronteira, end: original };
    if (sobra.end <= sobra.start) return;
    if ((sobra.end - sobra.start) * medida < MIN_WALL_PIECE) {
      span = ponta === 'start' ? { ...span, start: original } : { ...span, end: original };
      return;
    }
    const { label: _nome, notes: _notas, sideB: _outro, sideA: _lado, ...base } = casada;
    const nova = comTrecho(
      {
        ...base,
        id: novoId<'Wall'>(),
        sideA: { roomId, edgeIndex: geo.edgeIndex },
        joined: false,
        createdAt: now,
        updatedAt: now,
      },
      roomId,
      geo.edge,
      sobra,
    );
    labels[nova.id] = nomeDoPedaco(labels, walls, casada, roomId);
    walls = [...walls, nova];
  };

  ajustar(anterior, 'start');
  ajustar(seguinte, 'end');
  if (removidas.size === 0) return { draft: { ...draft, walls, labels }, span };

  for (const id of removidas) delete labels[id];
  return {
    draft: {
      ...draft,
      walls: walls.filter((parede) => !removidas.has(parede.id)),
      // O vão do pedaço que sumiu passa para a casada, que agora ocupa o lugar dele.
      openings: draft.openings.map((opening) =>
        removidas.has(opening.wallId) ? { ...opening, wallId: casada.id } : opening,
      ),
      elementSegments: semTrechoRepetido(
        draft.elementSegments.map((segment) =>
          removidas.has(segment.wallId) ? { ...segment, wallId: casada.id } : segment,
        ),
      ),
      labels,
    },
    span,
  };
}

/**
 * Os trechos do lado que nenhuma parede ocupa — onde o redesenho põe parede livre nova.
 *
 * O lado sem casada é um trecho só, o inteiro. O lado com a casada no meio são dois, um de
 * cada lado dela; a sobra menor que `MIN_WALL_PIECE` não vira parede.
 */
export function trechosLivres(ocupados: readonly EdgeSpan[], medida: number): readonly EdgeSpan[] {
  const ordenados = [...ocupados].sort((um, outro) => um.start - outro.start);
  const livres: EdgeSpan[] = [];
  let cursor = 0;
  for (const ocupado of ordenados) {
    if ((ocupado.start - cursor) * medida >= MIN_WALL_PIECE) {
      livres.push({ start: roundSpan(cursor), end: roundSpan(ocupado.start) });
    }
    cursor = Math.max(cursor, ocupado.end);
  }
  if ((1 - cursor) * medida >= MIN_WALL_PIECE) livres.push({ start: roundSpan(cursor), end: 1 });
  return livres;
}

// ── As candidatas a casar com esta ──────────────────────────────────────────────

/** Uma parede livre que pode casar com a selecionada, e por que ela está na lista. */
export interface CandidataParaCasar {
  readonly wall: Wall;
  /** De que tipologia ela é — outra tipologia faz divisa do pavimento (RN-163). */
  readonly typology: TypologyDraft;
  /** O afastamento entre as duas, em metros: a espessura que o desenho está mostrando. */
  readonly distance: number;
  /** Quanto as duas correm juntas, em metros — o que vai virar a parede casada. */
  readonly shared: number;
}

/**
 * As paredes livres que correm junto desta, da mais perto para a mais longe (RN-306).
 *
 * De outros ambientes — da mesma tipologia ou das outras do pavimento, que estão na mesma
 * prancha —, quase paralelas e a até `MANUAL_PAIRING_GAP`. A lista que oferecia toda parede
 * livre do apartamento, na ordem do cadastro, pedia que alguém achasse "Quarto · Lado 3"
 * entre quarenta nomes; a que vem pela distância põe a do outro lado do bloco em primeiro.
 *
 * Só quem tem lugar no desenho: sem contorno não há distância a medir, e a parede do
 * ambiente digitado continua casando pela lista do cartão de paredes.
 */
export function candidatasParaCasar(
  typologies: readonly TypologyDraft[],
  draft: TypologyDraft,
  wall: Wall,
): readonly CandidataParaCasar[] {
  if (wall.sideB !== undefined) return [];
  const minha = placeWalls(draft.rooms, draft.walls).find((posta) => posta.wallId === wall.id);
  if (minha === undefined) return [];

  const candidatas: CandidataParaCasar[] = [];
  for (const typology of typologies) {
    const livres = new Map(
      typology.walls
        .filter(
          (outra) =>
            outra.id !== wall.id &&
            outra.sideB === undefined &&
            outra.sideA.roomId !== wall.sideA.roomId,
        )
        .map((outra) => [String(outra.id), outra]),
    );
    for (const posta of placeWalls(typology.rooms, typology.walls)) {
      const outra = livres.get(String(posta.wallId));
      if (outra === undefined) continue;
      const trecho = sharedRun(minha.segment, posta.segment, MANUAL_PAIRING);
      if (trecho === undefined || trecho.length < MIN_WALL_PIECE) continue;
      candidatas.push({ wall: outra, typology, distance: trecho.gap, shared: trecho.length });
    }
  }
  return candidatas.sort(
    (uma, outra) => uma.distance - outra.distance || outra.shared - uma.shared,
  );
}
